<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-item"
          :class="'toast-' + toast.type"
        >
          <div class="toast-icon">
            <i :class="getIcon(toast.type)"></i>
          </div>
          <div class="toast-content">
            <p class="toast-message">{{ toast.message }}</p>
          </div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <i class="mdi mdi-close"></i>
          </button>
          <div class="toast-progress" :style="{ animationDuration: toast.duration + 'ms' }"></div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: "ToastNotification",
  data() {
    return {
      toasts: []
    };
  },
  methods: {
    getIcon(type) {
      const icons = {
        success: 'mdi mdi-check-circle',
        error: 'mdi mdi-alert-circle',
        warning: 'mdi mdi-alert',
        info: 'mdi mdi-information'
      };
      return icons[type] || icons.info;
    },
    
    addToast(message, type = 'info', duration = 4000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type, duration });
      
      setTimeout(() => {
        this.removeToast(id);
      }, duration);
      
      return id;
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(t => t.id === id);
      if (index > -1) {
        this.toasts.splice(index, 1);
      }
    },
    
    success(message, duration = 4000) {
      return this.addToast(message, 'success', duration);
    },
    
    error(message, duration = 5000) {
      return this.addToast(message, 'error', duration);
    },
    
    warning(message, duration = 4500) {
      return this.addToast(message, 'warning', duration);
    },
    
    info(message, duration = 4000) {
      return this.addToast(message, 'info', duration);
    }
  }
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 380px;
  width: 100%;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  backdrop-filter: blur(10px);
}

.toast-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}

.toast-icon i {
  font-size: 20px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a2e;
  line-height: 1.5;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  padding: 4px;
  margin: -4px -4px -4px 8px;
  cursor: pointer;
  color: #9ca3af;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #4b5563;
}

.toast-close i {
  font-size: 18px;
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  animation: progress linear forwards;
}

@keyframes progress {
  from { width: 100%; }
  to { width: 0%; }
}

/* Toast Types */
.toast-success {
  border-left: 4px solid #10b981;
}
.toast-success .toast-icon {
  color: #10b981;
}
.toast-success .toast-progress {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.toast-error {
  border-left: 4px solid #ef4444;
}
.toast-error .toast-icon {
  color: #ef4444;
}
.toast-error .toast-progress {
  background: linear-gradient(90deg, #ef4444, #f87171);
}

.toast-warning {
  border-left: 4px solid #f59e0b;
}
.toast-warning .toast-icon {
  color: #f59e0b;
}
.toast-warning .toast-progress {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.toast-info {
  border-left: 4px solid #3b82f6;
}
.toast-info .toast-icon {
  color: #3b82f6;
}
.toast-info .toast-progress {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

/* Transitions */
.toast-enter-active {
  animation: slideIn 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOut 0.3s ease-in;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Responsive */
@media (max-width: 480px) {
  .toast-container {
    left: 10px;
    right: 10px;
    max-width: none;
  }
}
</style>
